import { faArrowsRotate, faDistributeSpacingVertical, faFileExport, faFilter, faTableColumns } from '@fortawesome/pro-light-svg-icons';
import { faCirclePlus } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, IconButton, MenuItem, SelectChangeEvent, Select } from '@mui/material';
import { makeStyles } from 'tss-react/mui';

import Menu from '@mui/material/Menu';
import { GridToolbarColumnsButton, GridToolbarContainer, GridToolbarDensitySelector, GridToolbarExport, GridToolbarFilterButton } from '@mui/x-data-grid-pro';
import * as React from 'react';
import { Tooltip } from '../tooltip/tooltip';
import { TrashIcon } from '../icons';
import { ActiveListView, ActiveListViewContext } from './active-list-view-context';

/** HOW TO USE
 *
 * Initialize with this:
 * const CustomToolbar = () => FasitDataGridToolbar({ title: 'Peppe le pew', onAddButtonClick: yourFunctionThatDoesWhatever() });
 *
 * Reference CustomToolbar in your DataGrid rendering reference and fix the panel positioning:
 *
 * components={{
 *   Toolbar: CustomToolbar
 * }}
 * componentsProps={{
 *  panel: {
 *      disablePortal: true,
 *      placement: 'bottom-end',
 *  },
 * }}
 *
 * The add button is only shown if a function is passed to the onAddButtonClick property
 * The refresh button is only shown if a function is passed to the onRefreshClick property
 */

const useStyles = makeStyles()(() => ({
  topBar: {
    display: 'flex',
    justifyContent: 'flex-end',
    height: '45px',
  },
  delete: {
    backgroundColor: '#fbe0ea',
  },
}));
export interface IFasitDataGridToolbarMenuItem {
  onClick: () => void;
  label: string;
}

export interface IFilterValues {
  value: string;
  text: string;
}

export interface IFasitDataGridToolbarProps {
  title?: React.ReactNode;
  onAddButtonClick?: (event?: React.MouseEvent<HTMLButtonElement>) => void;
  addButtonTooltip?: string;
  showColumnButton?: boolean;
  showFilterButton?: boolean;
  showDensityButton?: boolean;
  showExportButton?: boolean;
  hidePrintButton?: boolean;
  addButtonMenuItems?: IFasitDataGridToolbarMenuItem[];
  customInput?: React.ReactNode;
  customInputBeforeFilter?: React.ReactNode;
  customSelectionInput?: React.ReactNode;
  selectionCount?: number;
  onRefreshClick?: () => void;
  refreshDisabled?: boolean;
  addButtonIcon?: React.ReactNode;
  disabled?: boolean;
  deleteMode?: boolean;
  onDeleteClick?: () => void;
  onFilterChange?: (event: SelectChangeEvent) => void;
  filterValues?: IFilterValues[];
  activeFilter?: string;
}

export function FasitDataGridToolbar(props: IFasitDataGridToolbarProps) {
  const {
    title,
    onAddButtonClick,
    addButtonTooltip = 'Opret',
    showColumnButton = true,
    showFilterButton = true,
    showDensityButton = true,
    showExportButton = false,
    hidePrintButton = false,
    addButtonMenuItems,
    customInput = null,
    customInputBeforeFilter = null,
    onRefreshClick,
    refreshDisabled = false,
    addButtonIcon,
    disabled = false,
    onDeleteClick,
    deleteMode,
    onFilterChange,
    filterValues,
    activeFilter,
    customSelectionInput,
    selectionCount = 0,
  } = props;
  const activeListView = React.useContext(ActiveListViewContext);

  let effectiveTitle = title;
  if (activeListView) {
    effectiveTitle = activeListView.view === 'active' ? activeListView.activeTitle : activeListView.allTitle;
  }
  const effectiveOnFilterChange = onFilterChange ?? (activeListView ? (event: SelectChangeEvent) => activeListView.onViewChange(event.target.value as ActiveListView) : undefined);
  const effectiveFilterValues =
    filterValues ??
    (activeListView
      ? [
          { value: 'active', text: 'Vis aktive' },
          { value: 'all', text: 'Vis alle' },
        ]
      : undefined);
  const effectiveActiveFilter = activeFilter ?? activeListView?.view;

  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const menuIsOpen = Boolean(anchorEl);
  function handleAddButtonClick(event?: React.MouseEvent<HTMLButtonElement>) {
    event && setAnchorEl(event.currentTarget);
  }
  function handleCloseMenu() {
    setAnchorEl(null);
  }
  function handleClickItem(item: IFasitDataGridToolbarMenuItem) {
    item && item.onClick();
    handleCloseMenu();
  }
  const hasSelection = selectionCount > 0;

  const showAddButton = (!!addButtonMenuItems || onAddButtonClick) && !hasSelection;

  const { classes: customClasses } = useStyles();
  return (
    <>
      {deleteMode && (
        <div className={`${customClasses.topBar} ${customClasses.delete}`}>
          {effectiveTitle && (
            <div className="FasitDataGridToolbar-title" style={{ alignSelf: 'center', marginLeft: '14px' }}>
              {effectiveTitle}
            </div>
          )}
          <Tooltip style={{ cursor: 'pointer' }} key="Remove" title="Fjern">
            <IconButton onClick={onDeleteClick} size="medium">
              <TrashIcon />
            </IconButton>
          </Tooltip>
        </div>
      )}
      {!deleteMode && (
        <GridToolbarContainer className={showAddButton ? 'hasAddButton' : ''} sx={{ backgroundColor: hasSelection ? '#fbe0ea' : undefined }}>
          {showAddButton && (
            <Tooltip title={addButtonTooltip}>
              <span>
                <IconButton
                  disabled={disabled}
                  onClick={addButtonMenuItems ? handleAddButtonClick : onAddButtonClick}
                  sx={{ fontSize: 20, marginRight: effectiveTitle ? 0 : 'auto' }}
                >
                  {addButtonIcon || <FontAwesomeIcon icon={faCirclePlus} />}
                </IconButton>
              </span>
            </Tooltip>
          )}
          {!hasSelection && effectiveTitle && <div className="FasitDataGridToolbar-title">{effectiveTitle}</div>}
          {customInputBeforeFilter}
          {effectiveOnFilterChange && effectiveFilterValues && (
            <Select sx={{ height: '35px', marginRight: '5px' }} value={effectiveActiveFilter} onChange={effectiveOnFilterChange}>
              {effectiveFilterValues.map(filterValue => (
                <MenuItem key={filterValue.value} value={filterValue.value}>
                  {filterValue.text}
                </MenuItem>
              ))}
            </Select>
          )}
          {customSelectionInput && selectionCount > 0 && customSelectionInput}
          {customInput}
          {showColumnButton && (
            <Tooltip title="Kolonner">
              <GridToolbarColumnsButton startIcon={<FontAwesomeIcon icon={faTableColumns} />} placeholder="" onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined} />
            </Tooltip>
          )}
          {showFilterButton && (
            <GridToolbarFilterButton
              componentsProps={{
                button: {
                  startIcon: <FontAwesomeIcon icon={faFilter} />,
                },
              }}
              placeholder=""
              onPointerEnterCapture={undefined}
              onPointerLeaveCapture={undefined}
            />
          )}
          {showDensityButton && (
            <Tooltip title="Tæthed">
              <GridToolbarDensitySelector
                startIcon={<FontAwesomeIcon icon={faDistributeSpacingVertical} />}
                placeholder=""
                onPointerEnterCapture={undefined}
                onPointerLeaveCapture={undefined}
              />
            </Tooltip>
          )}
          {showExportButton && (
            <Tooltip title="Eksportér">
              <GridToolbarExport startIcon={<FontAwesomeIcon icon={faFileExport} />} csvOptions={{ utf8WithBom: true }} printOptions={{ disableToolbarButton: hidePrintButton }} />
            </Tooltip>
          )}
          {onRefreshClick && (
            <Tooltip title="Opdatér liste">
              <span>
                <Button aria-label="Opdatér liste" startIcon={<FontAwesomeIcon icon={faArrowsRotate} />} onClick={onRefreshClick} disabled={refreshDisabled} sx={{ py: '4px', px: '5px' }} />
              </span>
            </Tooltip>
          )}
        </GridToolbarContainer>
      )}
      {addButtonMenuItems && !deleteMode && (
        <Menu
          id="basic-menu"
          anchorEl={anchorEl}
          open={menuIsOpen}
          onClose={handleCloseMenu}
          MenuListProps={{
            'aria-labelledby': 'basic-button',
          }}
        >
          {addButtonMenuItems.map((item: IFasitDataGridToolbarMenuItem) => (
            <MenuItem onClick={() => handleClickItem(item)} key={item.label}>
              {item.label}
            </MenuItem>
          ))}
        </Menu>
      )}
    </>
  );
}
